import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { useEffect, useState } from "react";

import {
  Auth,
  MapPage,
  Dashboard,
  Reporting,
  BulkAssignments,
  AssetDetails,
  AssetList,
} from "@/pages/index";
import { AuthProvider } from "@/context/authContext";
import { useAuth } from "@/hooks/useAuth";
import { SessionWarning } from "@/components/auth";
import { useActivityTracker } from "@/hooks/useActivityTracker";
import { supabase } from "@/supabase/supabaseClient";
import "./App.css";

// Smart root route handler - only redirects when user actually visits the root
function RootRouteHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    // Only handle initial app load to root
    try {
      const lastActiveRoute = localStorage.getItem("taskr-last-active-route");
      console.log(
        "RootRouteHandler: Retrieved last active route:",
        lastActiveRoute
      );

      if (
        lastActiveRoute &&
        lastActiveRoute !== "/" &&
        lastActiveRoute !== "/map"
      ) {
        console.log(
          "RootRouteHandler: Navigating to saved route:",
          lastActiveRoute
        );
        navigate(lastActiveRoute, { replace: true });
      } else {
        console.log("RootRouteHandler: Navigating to default /map");
        navigate("/map", { replace: true });
      }
    } catch (error) {
      console.warn("RootRouteHandler: Error reading localStorage:", error);
      navigate("/map", { replace: true });
    }
  }, [navigate]);

  return <div>Loading...</div>;
}

// Helper function to check for auth tokens in URL - runs synchronously
function checkAuthTokensInUrl() {
  // Check both query parameters (?key=value) and URL fragment (#key=value)
  const urlParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.substring(1));

  const type = urlParams.get("type") || hashParams.get("type");
  const token = urlParams.get("token") || hashParams.get("token");
  const accessToken =
    urlParams.get("access_token") || hashParams.get("access_token");

  // Detect password recovery flow
  const isRecovery = !!(
    (
      (token && type === "recovery" && !accessToken) || // Email reset link with token
      (accessToken && type === "recovery")
    ) // Magic link reset with access_token
  );

  // Detect invite flow (type=invite or type=signup for new users)
  const isInvite = !!(
    accessToken &&
    (type === "invite" || type === "signup" || type === "magiclink")
  );

  return { isRecovery, isInvite };
}

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();

  // Initialize with values from URL - runs synchronously on first render
  const [isPasswordResetFlow, setIsPasswordResetFlow] = useState<boolean>(
    () => checkAuthTokensInUrl().isRecovery
  );
  const [isInviteFlow, setIsInviteFlow] = useState<boolean>(
    () => checkAuthTokensInUrl().isInvite
  );
  const [signingOut, setSigningOut] = useState(false);

  // Sign out existing user if they're logged in and an invite flow is detected
  useEffect(() => {
    const handleInviteWithExistingSession = async () => {
      if (isInviteFlow && isAuthenticated && !signingOut) {
        console.log(
          "Invite flow detected with existing session - signing out current user"
        );
        setSigningOut(true);
        try {
          await supabase.auth.signOut();
          // After sign out, the auth state will update and show the invite flow
        } catch (error) {
          console.error("Error signing out for invite flow:", error);
        }
        setSigningOut(false);
      }
    };
    handleInviteWithExistingSession();
  }, [isInviteFlow, isAuthenticated, signingOut]);

  // Initialize activity tracker for logged-in users
  useActivityTracker({
    timeoutMinutes: 60, // 1 hour timeout
    warningMinutes: 5, // Show warning 5 minutes before timeout
  });

  if (loading || signingOut) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "#0f172a",
        }}
      >
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      <Routes>
        {/* Password reset flow takes priority over regular authentication */}
        {isPasswordResetFlow ? (
          <>
            <Route
              path="/auth"
              element={
                <Auth
                  isPasswordResetFlow={true}
                  onPasswordResetComplete={() => setIsPasswordResetFlow(false)}
                />
              }
            />
            <Route
              path="/reset-password"
              element={
                <Auth
                  isPasswordResetFlow={true}
                  onPasswordResetComplete={() => setIsPasswordResetFlow(false)}
                />
              }
            />
            <Route
              path="/signin"
              element={
                <Auth
                  isPasswordResetFlow={true}
                  onPasswordResetComplete={() => setIsPasswordResetFlow(false)}
                />
              }
            />
            <Route
              path="*"
              element={<Navigate to="/reset-password" replace />}
            />
          </>
        ) : isInviteFlow ? (
          <>
            {/* Invite flow - user needs to set their password */}
            <Route
              path="*"
              element={
                <Auth
                  isInviteFlow={true}
                  onInviteComplete={() => setIsInviteFlow(false)}
                />
              }
            />
          </>
        ) : !isAuthenticated ? (
          <>
            {/* OAuth callback route at root path to preserve hash fragments */}
            <Route path="/" element={<Auth isPasswordResetFlow={false} />} />
            <Route
              path="/signin"
              element={<Auth isPasswordResetFlow={false} />}
            />
            <Route
              path="/auth"
              element={<Auth isPasswordResetFlow={false} />}
            />
            <Route
              path="/reset-password"
              element={<Auth isPasswordResetFlow={false} />}
            />
            <Route path="*" element={<Navigate to="/signin" replace />} />
          </>
        ) : (
          <>
            <Route path="/map" element={<MapPage />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/asset-management" element={<AssetList />} />
            <Route
              path="/assets-management/assets_lifecycle/:assetId"
              element={<AssetDetails />}
            />
            <Route path="/reporting" element={<Reporting />} />
            <Route path="/bulk-assignments" element={<BulkAssignments />} />
            <Route path="/" element={<RootRouteHandler />} />
            <Route path="*" element={<Navigate to="/map" replace />} />
          </>
        )}
      </Routes>
      <SessionWarning />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;
